import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
} from "../../../components/ui";
import {
  fetchActivityTemplates,
  createActivityTemplate,
  updateActivityTemplate,
  fetchPipelineStages,
  fetchRoles,
} from "../../../services/crmSettings";

const emptyForm = {
  code: "",
  label: "",
  pipeline_stage_code: "",
  role_code: "",
  due_days: 0,
  description: "",
  is_active: true,
  sort_order: 100,
};

export default function ActivityTemplatesSettings() {
  const [items, setItems] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [roles, setRoles] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    try {
      setLoading(true);
      setError("");

      const [templatesRows, stageRows, roleRows] = await Promise.all([
        fetchActivityTemplates(),
        fetchPipelineStages(),
        fetchRoles(),
      ]);

      setItems(Array.isArray(templatesRows) ? templatesRows : []);
      setPipelineStages(Array.isArray(stageRows) ? stageRows : []);
      setRoles(Array.isArray(roleRows) ? roleRows : []);
    } catch (e) {
      setError(e.message || "Failed to load activity templates");
      setItems([]);
      setPipelineStages([]);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }

  const activePipelineStages = useMemo(() => {
    return (pipelineStages || [])
      .filter((x) => Number(x.is_active) === 1 || x.is_active === true)
      .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
  }, [pipelineStages]);

  const activeRoles = useMemo(() => {
    return (roles || [])
      .filter((x) => Number(x.is_active) === 1 || x.is_active === true)
      .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
  }, [roles]);

  function startEdit(item) {
    setEditingId(item.id);
    setForm({
      code: item.code || "",
      label: item.label || "",
      pipeline_stage_code: item.pipeline_stage_code || "",
      role_code: item.role_code || "",
      due_days: item.due_days ?? 0,
      description: item.description || "",
      is_active: !!item.is_active,
      sort_order: item.sort_order ?? 100,
    });
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyForm);
  }

  async function onSubmit(e) {
    e.preventDefault();

    try {
      setSaving(true);
      setError("");

      const payload = {
        code: String(form.code || "").trim() || null,
        label: String(form.label || "").trim(),
        pipeline_stage_code: String(form.pipeline_stage_code || "").trim() || null,
        role_code: String(form.role_code || "").trim() || null,
        due_days: Number(form.due_days || 0),
        description: String(form.description || "").trim() || null,
        is_active: form.is_active ? 1 : 0,
        sort_order: Number(form.sort_order || 100),
      };

      if (!payload.label) {
        throw new Error("Label is required");
      }

      if (editingId) {
        await updateActivityTemplate(editingId, payload);
      } else {
        await createActivityTemplate(payload);
      }

      resetForm();
      await loadAll();
    } catch (e) {
      setError(e.message || "Failed to save activity template");
    } finally {
      setSaving(false);
    }
  }

  function pipelineLabel(code) {
    const found = activePipelineStages.find((x) => x.code === code);
    return found?.label || code || "—";
  }

  function roleLabel(code) {
    const found = activeRoles.find((x) => x.code === code);
    return found?.name || found?.label || code || "—";
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity Templates</CardTitle>
        <CardDescription>
          Define automatic activity templates by pipeline stage and role.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="rounded-xl border">
          <div className="p-4 border-b">
            <div className="font-medium">Template list</div>
            <div className="text-sm text-muted-foreground">
              Configure which activities should be prepared for each stage and role.
            </div>
          </div>

          <div className="p-4">
            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-sm text-muted-foreground">Loading...</div>
            ) : items.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No activity templates found.
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="rounded-xl border p-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-medium">{item.label}</div>

                        {item.code && (
                          <span className="text-xs rounded-md border px-2 py-0.5">
                            {item.code}
                          </span>
                        )}

                        {!item.is_active && (
                          <span className="text-xs rounded-md border px-2 py-0.5 text-muted-foreground">
                            inactive
                          </span>
                        )}
                      </div>

                      <div className="mt-2 text-sm text-muted-foreground">
                        {item.description || "—"}
                      </div>

                      <div className="mt-2 text-xs text-muted-foreground flex flex-wrap gap-3">
                        <span>Stage: {pipelineLabel(item.pipeline_stage_code)}</span>
                        <span>Role: {roleLabel(item.role_code)}</span>
                        <span>Due: {Number(item.due_days || 0)} day(s)</span>
                        <span>Sort: {item.sort_order ?? "—"}</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        type="button"
                        onClick={() => startEdit(item)}
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl border">
          <div className="p-4 border-b">
            <div className="font-medium">
              {editingId ? "Edit activity template" : "New activity template"}
            </div>
            <div className="text-sm text-muted-foreground">
              Create or update workflow activity template.
            </div>
          </div>

          <form onSubmit={onSubmit} className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-sm mb-1">Code</div>
                <Input
                  value={form.code}
                  onChange={(e) => setForm((s) => ({ ...s, code: e.target.value }))}
                  placeholder="proposal_design"
                />
              </div>

              <div>
                <div className="text-sm mb-1">Label</div>
                <Input
                  value={form.label}
                  onChange={(e) => setForm((s) => ({ ...s, label: e.target.value }))}
                  placeholder="Prepare initial design"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <div className="text-sm mb-1">Pipeline stage</div>
                <select
                  className="h-9 w-full rounded-md px-3 bg-white dark:bg-neutral-900 border border-gray-400 dark:border-neutral-700"
                  value={form.pipeline_stage_code}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, pipeline_stage_code: e.target.value }))
                  }
                >
                  <option value="">—</option>
                  {activePipelineStages.map((stage) => (
                    <option key={stage.id} value={stage.code}>
                      {stage.label || stage.code}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="text-sm mb-1">Role</div>
                <select
                  className="h-9 w-full rounded-md px-3 bg-white dark:bg-neutral-900 border border-gray-400 dark:border-neutral-700"
                  value={form.role_code}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, role_code: e.target.value }))
                  }
                >
                  <option value="">—</option>
                  {activeRoles.map((role) => (
                    <option key={role.id} value={role.code || role.name}>
                      {role.name || role.label || role.code}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="text-sm mb-1">Due days</div>
                <Input
                  type="number"
                  value={form.due_days}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, due_days: Number(e.target.value || 0) }))
                  }
                />
              </div>
            </div>

            <div>
              <div className="text-sm mb-1">Description</div>
              <textarea
                rows={3}
                className="w-full rounded-md border px-3 py-2 bg-white dark:bg-neutral-900"
                value={form.description}
                onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                placeholder="Short description..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-sm mb-1">Sort order</div>
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, sort_order: Number(e.target.value || 100) }))
                  }
                />
              </div>

              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, is_active: e.target.checked }))
                    }
                  />
                  Active
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              {editingId && (
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              )}

              <Button type="submit" disabled={saving}>
                {saving
                  ? "Saving..."
                  : editingId
                  ? "Update activity template"
                  : "Create activity template"}
              </Button>
            </div>
          </form>
        </div>
      </CardContent>
    </Card>
  );
}